import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCartStore, useWishlistStore } from '@/store';
import { ShoppingCart, Heart, User, Menu, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';
import { shopifyFetch } from '@/shopify';
import { normalizeText, getSearchVariants } from '@/lib/search';

const navLinks = [
  { to: '/produtos', label: 'Produtos' },
  { to: '/sobre', label: 'Sobre' },
  { to: '/contato', label: 'Contato' }
];

export const Navbar = () => {
  const { customer, isLoggedIn, logout } = useCustomerAuth();
  const { getItemCount } = useCartStore();
  const { getWishlistCount } = useWishlistStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const debounceRef = useRef(null);
  const profileRef = useRef(null);

  const cartCount = getItemCount();
  const wishlistCount = getWishlistCount();

  useEffect(() => {
    async function fetchSearchProducts() {
      try {
        const data = await shopifyFetch(`
          query {
            products(first: 50) {
              edges {
                node {
                  id
                  title
                  handle
                  productType
                  featuredImage {
                    url
                    altText
                  }
                }
              }
            }
          }
        `);

        const formatted =
          data?.products?.edges?.map(({ node }) => ({
            id: node.id,
            name: node.title,
            handle: node.handle,
            category: node.productType || 'Sem categoria',
            image_url: node.featuredImage?.url || ''
          })) || [];

        setAllProducts(formatted);
      } catch (error) {
        void error;
      }
    }

    fetchSearchProducts();
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const suggestions = useMemo(() => {
    if (!debouncedQuery.trim()) return [];

    const searchWords = normalizeText(debouncedQuery).split(' ').filter(Boolean);
    const variantsList = searchWords.map(getSearchVariants);

    return allProducts
      .filter((product) => {
        const name = normalizeText(product.name);
        const category = normalizeText(product.category);

        return variantsList.every((variants) =>
          variants.some(
            (variant) =>
              name.includes(variant) ||
              category.includes(variant)
          )
        );
      })
      .slice(0, 6);
  }, [debouncedQuery, allProducts]);

  const isNavActive = (path) => {
    if (path === '/produtos') {
      return location.pathname === '/produtos' || location.pathname.startsWith('/produto/');
    }

    return location.pathname === path;
  };

  const navLinkClass = (path) =>
    `font-body text-sm uppercase tracking-widest border-b pb-1 transition-colors duration-200 ${
      isNavActive(path)
        ? 'text-accent border-accent'
        : 'text-text-primary border-transparent hover:text-accent'
    }`;

  const mobileLinkClass = (path) =>
    `block rounded-lg px-3 py-2 text-base transition-colors ${
      isNavActive(path)
        ? 'bg-accent/10 text-accent'
        : 'text-text-primary hover:bg-stone-50'
    }`;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, 250);
  };

  const handleSearch = (e) => {
    e.preventDefault();

    if (searchQuery.trim()) {
      navigate(`/produtos?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSuggestions(false);
      setSearchQuery('');
      setDebouncedQuery('');
      setMobileMenuOpen(false);
    }
  };

  const handleSuggestionClick = (product) => {
    navigate(`/produto/${encodeURIComponent(product.id)}`);
    setSearchQuery('');
    setDebouncedQuery('');
    setShowSuggestions(false);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  const renderSearchSuggestions = () => (
    <AnimatePresence>
      {showSuggestions && suggestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.18 }}
          className="absolute top-full mt-3 left-0 w-full bg-white rounded-xl shadow-lg border border-stone-100 overflow-hidden z-50"
        >
          {suggestions.map((product) => (
            <button
              key={product.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSuggestionClick(product)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 text-left transition-colors cursor-pointer"
            >
              <div className="w-12 h-12 bg-stone-100 rounded-lg overflow-hidden flex-shrink-0">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>

              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">
                  {product.name}
                </p>
                <p className="text-xs text-text-secondary truncate">
                  {product.category}
                </p>
              </div>
            </button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <nav
      className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-stone-100"
      data-testid="main-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/"
            className="font-heading text-2xl font-bold tracking-widest text-accent shrink-0"
            data-testid="brand-logo"
          >
            AURA ROSA
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to} className={navLinkClass(link.to)}>
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-6">
            <div className="relative">
              <form onSubmit={handleSearch} className="relative">
                <label htmlFor="desktop-product-search" className="sr-only">
                  Buscar produtos
                </label>
                <Input
                  id="desktop-product-search"
                  type="search"
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => {
                    if (searchQuery.trim()) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  className="w-56 pl-10 rounded-full border-stone-200 focus:border-accent"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              </form>

              {renderSearchSuggestions()}
            </div>

            <Link
              to="/wishlist"
              className="relative hover:text-accent transition-colors"
              aria-label={`Lista de desejos com ${wishlistCount} itens`}
            >
              <Heart className="w-5 h-5" />
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </Link>

            <Link
              to="/carrinho"
              className="relative hover:text-accent transition-colors"
              aria-label={`Carrinho com ${cartCount} itens`}
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {isLoggedIn ? (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen((prev) => !prev)}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                  className="flex items-center gap-2 px-0 py-0 text-text-primary hover:text-accent transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded"
                >
                  <User className="w-5 h-5" />
                  <span className="text-sm whitespace-nowrap">
                    Olá, {customer?.firstName || 'Cliente'}
                  </span>
                </button>

                <AnimatePresence>
                  {profileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.18 }}
                      className="absolute right-0 top-full mt-3 w-52 bg-white rounded-xl shadow-lg z-50 overflow-hidden border border-stone-100"
                      role="menu"
                    >
                      <Link
                        to="/perfil"
                        onClick={() => setProfileOpen(false)}
                        className="block px-4 py-3 hover:bg-gray-50"
                        role="menuitem"
                      >
                        Meu Perfil
                      </Link>
                      <Link
                        to="/pedidos"
                        onClick={() => setProfileOpen(false)}
                        className="block px-4 py-3 hover:bg-gray-50"
                        role="menuitem"
                      >
                        Meus Pedidos
                      </Link>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        role="menuitem"
                      >
                        Sair
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to="/login">
                <Button className="bg-accent text-white hover:bg-accent/90 rounded-full px-6">
                  Entrar
                </Button>
              </Link>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden rounded-full p-2 hover:bg-stone-50 transition-colors cursor-pointer"
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="md:hidden overflow-hidden"
            >
              <div className="mt-4 pb-4 space-y-4">
                <div className="relative">
                  <form onSubmit={handleSearch} className="relative">
                    <label htmlFor="mobile-product-search" className="sr-only">
                      Buscar produtos
                    </label>
                    <Input
                      id="mobile-product-search"
                      type="search"
                      placeholder="Buscar produtos..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onFocus={() => {
                        if (searchQuery.trim()) {
                          setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                      className="w-full rounded-full pl-10"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  </form>
                  {renderSearchSuggestions()}
                </div>

                <div className="space-y-1">
                  {navLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={mobileLinkClass(link.to)}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/wishlist"
                    className={mobileLinkClass('/wishlist')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Lista de Desejos
                    {wishlistCount > 0 ? ` (${wishlistCount})` : ''}
                  </Link>
                  <Link
                    to="/carrinho"
                    className={mobileLinkClass('/carrinho')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Carrinho
                    {cartCount > 0 ? ` (${cartCount})` : ''}
                  </Link>
                </div>

                <div className="border-t border-stone-100 pt-4 space-y-1">
                  {isLoggedIn ? (
                    <>
                      <div className="px-3 py-2 text-sm text-text-secondary">
                        Olá, {customer?.firstName || 'Cliente'}
                      </div>
                      <Link
                        to="/perfil"
                        className={mobileLinkClass('/perfil')}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Meu Perfil
                      </Link>
                      <Link
                        to="/pedidos"
                        className={mobileLinkClass('/pedidos')}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Meus Pedidos
                      </Link>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="block w-full text-left rounded-lg px-3 py-2 hover:bg-stone-50 cursor-pointer"
                      >
                        Sair
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className={mobileLinkClass('/login')}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Entrar
                      </Link>
                      <Link
                        to="/register"
                        className="block rounded-lg px-3 py-2 text-accent hover:bg-accent/10 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Criar conta
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};
